import type { ReactNode } from 'react';
import { Skeleton } from '../../components/ui/skeleton';
import { cn } from '../../lib/utils';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import {
  resolveLinkPreview,
  ALL_PREVIEW_PLATFORMS,
  ALLOWED_PLATFORMS_BY_MODE,
  type LinkPreview,
  type PreviewPlatform,
  type RequirePlatform,
} from '../../lib/linkPreview';

// Live, FREE preview for the "Add content URL" modal. Every platform renders
// through its own official EMBED IFRAME — no API calls, no keys, no backend,
// no scrapers. The iframe itself surfaces whatever metadata the platform
// exposes (YouTube: title + channel + duration in the player; Instagram: image
// + caption + author; Drive: the file rendered inline).
//
// Every branch is a function that RETURNS the box — there are no conditionals
// inside JSX tags; the correct renderer is selected by a switch on the
// resolved descriptor.

const FRAME_ALLOW =
  'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';

// Hardening applied to every embed frame: don't leak the app's full URL to the
// third-party origin, and defer the load until the frame is near the viewport.
const FRAME_HARDENING = {
  referrerPolicy: 'strict-origin-when-cross-origin',
  loading: 'lazy',
} as const;

// Which preview platforms each require-platform mode is allowed to render.
// Reads from the SAME map UrlInputModal.validateUrl enforces at Save — the
// preview can never lend legitimacy to a URL that Save would then reject.
// `undefined` (any URL) allows every previewable platform; a mode with no
// embeddable preview here.
function allowedPlatforms(requirePlatform?: RequirePlatform): ReadonlySet<PreviewPlatform> {
  if (!requirePlatform) return ALL_PREVIEW_PLATFORMS;
  return ALLOWED_PLATFORMS_BY_MODE[requirePlatform] ?? new Set<PreviewPlatform>();
}

// The platform a resolved descriptor is "about", or null for the kinds that map
// to no platform (empty / unsupported — those render nothing regardless).
function previewPlatform(preview: LinkPreview): PreviewPlatform | null {
  switch (preview.kind) {
    case 'youtube':
      return 'youtube';
    case 'drive':
      return 'drive';
    case 'instagram':
      return 'instagram';
    case 'pending':
    case 'unpreviewable':
      return preview.platform;
    case 'empty':
    case 'unsupported':
      return null;
  }
}

// Shared flex box for every preview. The top margin lives here (not on a modal
// wrapper) so that when nothing is previewable and the card returns null, no
// empty spacer is left behind.
function shell(extra: string): string {
  return cn('mt-3 flex rounded-xl border border-border bg-muted p-3', extra);
}

// YouTube: the embed player shows title, channel and duration natively — free,
// no fetch.
function YouTubePreview({ preview }: { preview: Extract<LinkPreview, { kind: 'youtube' }> }) {
  return (
    <div className={shell('flex-col')}>
      <div className="aspect-video w-full overflow-hidden rounded-lg bg-black">
        <iframe
          title="YouTube video"
          src={preview.embedUrl}
          className="size-full"
          allow={FRAME_ALLOW}
          allowFullScreen
          {...FRAME_HARDENING}
        />
      </div>
    </div>
  );
}

// Drive: Google renders the file itself inside the /preview iframe (name/toolbar
// included where Drive shows it). No key.
function DrivePreview({ preview }: { preview: Extract<LinkPreview, { kind: 'drive' }> }) {
  return (
    <div className={shell('flex-col')}>
      <div className="aspect-video w-full overflow-hidden rounded-lg bg-black">
        <iframe
          title="Google Drive file"
          src={preview.embedUrl}
          className="size-full"
          allow={FRAME_ALLOW}
          allowFullScreen
          {...FRAME_HARDENING}
        />
      </div>
    </div>
  );
}

// Instagram: official public embed — Instagram renders the post (image, caption,
// author) inside its own boxed card. Compact width for a tidy preview.
function InstagramPreview({
  preview,
}: {
  preview: Extract<LinkPreview, { kind: 'instagram' }>;
}) {
  return (
    <div className={shell('justify-center')}>
      <iframe
        title="Instagram post"
        src={preview.embedUrl}
        className="h-[420px] w-full max-w-[300px] overflow-hidden rounded-lg border-0 bg-white"
        scrolling="no"
        allow={FRAME_ALLOW}
        {...FRAME_HARDENING}
      />
    </div>
  );
}

// A previewable path whose id isn't complete yet (user mid-entry) — skeleton.
function PendingPreview() {
  return (
    <div className={shell('flex-col')}>
      <Skeleton className="aspect-video w-full rounded-lg" />
    </div>
  );
}

// A recognised host we can't embed (a channel, a Drive folder, an IG profile).
// A settled state, not a spinner — tell the user plainly; the URL still saves.
function UnpreviewablePreview() {
  return (
    <div className={shell('items-center gap-2')}>
      <p className="text-caption-regular text-muted-foreground">
        We can’t preview this link, but it will still be saved.
      </p>
    </div>
  );
}

// Selects the renderer for a resolved descriptor. Returns null for the
// non-visual kinds — a plain function return, never a condition in a tag.
function renderPreview(preview: LinkPreview): ReactNode {
  switch (preview.kind) {
    case 'youtube':
      return <YouTubePreview preview={preview} />;
    case 'drive':
      return <DrivePreview preview={preview} />;
    case 'instagram':
      return <InstagramPreview preview={preview} />;
    case 'pending':
      return <PendingPreview />;
    case 'unpreviewable':
      return <UnpreviewablePreview />;
    case 'empty':
    case 'unsupported':
      return null;
  }
}

export function LinkPreviewCard({
  url,
  requirePlatform,
}: {
  url: string;
  requirePlatform?: RequirePlatform;
}) {
  const debouncedUrl = useDebouncedValue(url, 400);
  const preview = resolveLinkPreview(debouncedUrl);

  // Gate the preview by the same platform rule Save enforces. A descriptor for a
  // platform this mode disallows renders nothing — no working embed for a URL
  // that would be rejected on Save.
  const platform = previewPlatform(preview);
  if (platform && !allowedPlatforms(requirePlatform).has(platform)) return null;

  return renderPreview(preview);
}
